// ==========================================
// TIMBER FRAME BIDDING SYSTEM
// ==========================================

// Main Bid interface with grouped structure
export interface Bid {
  id: string;
  bidNumber: string; // Auto-generated: "BID-2026-001"
  
  // Relationships
  leadId?: string;
  dealId?: string;
  contactId?: string;
  projectName: string;
  
  // Grouped structure - each bid can have multiple groups
  groups: BidGroup[];
  
  // Document uploads (shared across all groups)
  sourceDocuments: SourceDocument[];
  
  // Workflow
  status: BidStatus;
  version: number;
  parentBidId?: string; // If this is a revision of another bid
  
  // Metadata
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  sentAt?: Date;
  respondedAt?: Date;
  
  // Proposal
  proposalUrl?: string; // Firebase Storage URL for generated PDF
  notes?: string;
}

export interface SourceDocument {
  name: string;
  url: string; // Firebase Storage URL
  uploadedAt: Date;
  type: 'pdf' | 'image' | 'csv';
}

export type BidStatus = 'draft' | 'pending-review' | 'sent' | 'accepted' | 'rejected' | 'revised';

// ==========================================
// BID GROUP - Each group has all 5 buckets
// ==========================================

export interface BidGroup {
  id: string;
  name: string; // e.g., "Front Porch", "Back Porch", "Main Structure"
  order: number; // Display order in UI
  
  // Timber list for this specific group
  timberList: TimberItem[];
  
  // 5 Cost Buckets
  designBucket: DesignBucket;
  productionBucket: ProductionBucket;
  freightBucket: FreightBucket;
  installBucket: InstallBucket;
  materialsBucket: MaterialsBucket;
  
  // Group totals
  subtotal: number; // Sum of all 5 buckets
  profitMargin: number; // Default 0.50 (50%), adjustable per group
  total: number; // subtotal × (1 + profitMargin)
}

// ==========================================
// TIMBER LIST
// ==========================================

export interface TimberItem {
  id: string;
  prodNumber?: string; // From CSV PROD # column
  name: string; // "post", "corbel", "rafter", "arch", "brace", etc.
  group?: string; // From CSV GROUP column (optional, for categorization)
  material: string; // "#1 GRN DF" (Grade #1 Green Douglas Fir), etc.
  quantity: number; // Number of pieces
  width: number; // inches
  height: number; // inches
  realLength: string; // From CSV (complex format, actual material used)
  orderLength: string; // From CSV (what gets ordered, standard lengths)
  
  // Calculated fields
  boardFeet: number; // (width × height × orderLength in inches) ÷ 144
  unitPrice: number; // Per piece price from Google Sheets
  totalPrice: number; // quantity × unitPrice
  
  // Metadata
  confidence?: number; // OCR confidence score (0-1)
  manuallyEdited: boolean; // Whether user edited OCR results
  assignedToGroup?: string; // Group ID this item is assigned to
}

// ==========================================
// BUCKET 1: DESIGN
// ==========================================

export interface DesignBucket {
  lineItems: DesignLineItem[];
  total: number; // Sum of all line item costs
}

export interface DesignLineItem {
  id: string;
  category: 'design' | 'engineering' | 'overhead'; // Type of design cost
  description: string; // e.g., "Timber Frame Design", "Structural Engineering"
  dayRate: number; // $/day
  days: number; // estimated days
  cost: number; // dayRate × days (auto-calculated)
  billableTotal: number; // cost with any markup applied
}

// Default design day rates
export const DEFAULT_DESIGN_DAY_RATES = {
  design: 600, // Design work day rate
  engineering: 1200, // Engineering day rate
  overhead: 200, // Overhead per day
};

// ==========================================
// BUCKET 2: PRODUCTION
// ==========================================

export interface ProductionBucket {
  totalBoardFeet: number; // Sum of board feet from timber list
  ratePerBoardFoot: number; // Fixed at $2.00
  total: number; // totalBoardFeet × ratePerBoardFoot (auto-calculated)
}

export const PRODUCTION_RATE_PER_BOARD_FOOT = 2.0;

// ==========================================
// BUCKET 3: FREIGHT
// ==========================================

export interface FreightBucket {
  inbound: number; // Cost to ship to PA shop (manual entry)
  outbound: number; // Cost to ship to site (manual entry)
  total: number; // inbound + outbound (auto-calculated)
}

// ==========================================
// BUCKET 4: INSTALL
// ==========================================

export interface InstallBucket {
  totalInstallDays: number; // Drives daily rate calculations
  lineItems: InstallLineItem[];
  total: number; // Sum of all line item billable totals
}

export interface InstallLineItem {
  id: string;
  type: 'labor' | 'equipment' | 'overhead';
  description: string; // "Joey", "David", "Crane", "Telehandler", "Overhead"
  rate: number; // $/hr or $/day
  rateUnit: 'hour' | 'day';
  quantity: number; // hours or days worked
  cost: number; // rate × quantity (auto-calculated)
  billableTotal: number; // cost with markup applied
}

// ==========================================
// BUCKET 5: MATERIALS
// ==========================================

export interface MaterialsBucket {
  timbers: TimberMaterialsSummary; // Auto-calculated from timber list
  manualItems: ManualMaterialItem[]; // Fasteners, post bases, pegs, etc.
  total: number; // timbers.totalCost + sum of manual items
}

export interface TimberMaterialsSummary {
  items: TimberItem[]; // Reference to group's timber list
  totalCost: number; // Sum of all timber item totalPrices
}

export interface ManualMaterialItem {
  id: string;
  description: string; // "Fasteners", "Post bases", "Pegs", "Hardware"
  quantity: number;
  unit: string; // "ea", "lb", "box", etc.
  unitPrice: number;
  totalPrice: number; // quantity × unitPrice (auto-calculated)
}

// ==========================================
// BID SETTINGS
// ==========================================

export interface BidSettings {
  // Google Sheets pricing integration
  materialPricingSheetUrl: string;
  lastPricingUpdate?: Date;
  pricingCache?: PricingCache;
  
  // Fixed rates
  productionRatePerBoardFoot: number; // $2.00
  
  // Default design rates
  defaultDesignRates: {
    matt: number;
    alex: number;
    dan: number;
    engineering: number;
  };
  
  // Default profit margin
  defaultProfitMargin: number; // 0.50 (50%)
  
  // Google Cloud Vision API key
  visionApiKey?: string;
}

export interface PricingCache {
  [key: string]: { // key format: "name-material-width-height-orderLength"
    unitPrice: number;
    lastUpdated: Date;
  };
}

// ==========================================
// GOOGLE SHEETS PRICING
// ==========================================

export interface MaterialPricing {
  name: string; // "post", "corbel", "rafter", etc.
  material: string; // "#1 GRN DF", etc.
  width: number; // inches
  height: number; // inches
  orderLength: string; // "8'", "10'", "12'", etc.
  unitPrice: number; // Price per piece
}

// ==========================================
// OCR RESULTS
// ==========================================

export interface OCRResult {
  text: string; // Full extracted text
  confidence: number; // Overall confidence (0-1)
  extractedItems: TimberItem[]; // Parsed timber items
  warnings?: string[]; // Any parsing warnings or low-confidence items
}

export interface OCRTextAnnotation {
  description: string;
  boundingPoly?: {
    vertices: Array<{ x: number; y: number }>;
  };
}

// ==========================================
// BOARD FOOT CALCULATIONS
// ==========================================

export interface BoardFootCalculation {
  item: TimberItem;
  lengthInInches: number; // Parsed from orderLength
  boardFeet: number; // (width × height × lengthInInches) ÷ 144
}

// ==========================================
// PROPOSAL TEMPLATE
// ==========================================

export interface ProposalData {
  bid: Bid;
  companyInfo?: CompanyInfo;
  clientInfo?: ClientInfo;
  generatedAt: Date;
}

export interface CompanyInfo {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logoUrl?: string;
}

export interface ClientInfo {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
}

// ==========================================
// TIMBER LIST PARSER
// ==========================================

export interface ParsedTimberList {
  items: TimberItem[];
  metadata?: {
    fileName?: string;
    dateDesigned?: string;
    designer?: string;
  };
  parseErrors?: string[];
}

// ==========================================
// HELPER TYPES
// ==========================================

export interface BidSummary {
  bidId: string;
  bidNumber: string;
  projectName: string;
  status: BidStatus;
  grandTotal: number;
  groupCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface GroupSummary {
  groupId: string;
  groupName: string;
  timberItemCount: number;
  totalBoardFeet: number;
  subtotal: number;
  profitMargin: number;
  total: number;
}

// ==========================================
// VALIDATION TYPES
// ==========================================

export interface BidValidationError {
  field: string;
  message: string;
  groupId?: string;
  itemId?: string;
}

export interface BidValidationResult {
  isValid: boolean;
  errors: BidValidationError[];
  warnings?: BidValidationError[];
}

// ==========================================
// EXPORT FORMATS
// ==========================================

export type ExportFormat = 'pdf' | 'csv' | 'excel';

export interface ExportOptions {
  format: ExportFormat;
  includeTimberList: boolean;
  includeDetailedBreakdown: boolean;
  includeImages: boolean;
}
